import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';

import { KnowledgeOverview } from './KnowledgeOverview';
import { KnowledgeStorage } from './KnowledgeStorage';
import { KnowledgePipelines } from './KnowledgePipelines';
import { KnowledgeEmbeddings } from './KnowledgeEmbeddings';
import { MemoryPanel } from '@/features/Memory/components/MemoryPanel';
import { useKnowledge } from '../hooks/useKnowledge';
import { useQueryClient } from '@tanstack/react-query';
import {
  BRAIN_SETTINGS_OPEN,
  BRAIN_SETTINGS_PARAM,
  brainTabParam,
  isLegacySecurityTab,
  isLegacySoulTab,
  resolveBrainTab,
  workspaceSoulPath,
  type BrainLocation,
  type BrainTab,
  type KnowledgeTab,
} from '../utils/brainTabs';

/**
 * O Brain em camadas (AB#86817, AB#87149): Mind, Memory e Knowledge, da mais rígida para a mais
 * livre. A Soul, acima das duas, é a descrição do workspace e vive nas
 * configurações dele. A posição vive no `?tab=`, para os links salvos continuarem valendo.
 */
const Knowledge = () => {
  const { t } = useTranslation();
  const { data: activeKnowledgeBase } = useKnowledge();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useMemo(() => resolveBrainTab(searchParams.get('tab')), [searchParams]);
  const { isSuperAdmin } = useIsSuperAdminRole();
  const { currentWorkspace } = useWorkspace();

  const goTo = (next: BrainLocation) => {
    setSearchParams(
      (params) => {
        params.set('tab', brainTabParam(next));
        return params;
      },
      { replace: true }
    );
  };

  // Refetch dados quando abrir o armazenamento da Knowledge
  useEffect(() => {
    if (location.tab === "knowledge" && location.knowledgeTab === "files" && activeKnowledgeBase?.id) {
      queryClient.invalidateQueries({
        queryKey: ["knowledge-storage", activeKnowledgeBase.id],
      });
    }
  }, [location, activeKnowledgeBase, queryClient]);

  // Link salvo da antiga aba Security: a Mind, com as configurações do Brain abertas.
  useEffect(() => {
    if (!isLegacySecurityTab(searchParams.get('tab'))) return;
    setSearchParams(
      (params) => {
        params.set('tab', 'mind');
        params.set(BRAIN_SETTINGS_PARAM, BRAIN_SETTINGS_OPEN);
        return params;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  // Link salvo da antiga aba Soul: a Soul agora vive nas configurações do workspace.
  // Sem workspace ativo, o resolveBrainTab já cai na Mind.
  if (isLegacySoulTab(searchParams.get('tab')) && currentWorkspace?.id) {
    return <Navigate to={workspaceSoulPath(currentWorkspace.id)} replace />;
  }

  return (
    <div className="space-y-6">
      <Tabs
        value={location.tab}
        className="w-full"
        onValueChange={(value) => goTo({ ...location, tab: value as BrainTab })}
      >
        <TabsList>
          <TabsTrigger value="mind">{t('knowledge.brain.tabs.mind')}</TabsTrigger>
          {currentWorkspace?.id && (
            <TabsTrigger value="memory">{t('knowledge.brain.tabs.memory')}</TabsTrigger>
          )}
          <TabsTrigger value="knowledge">{t('knowledge.brain.tabs.knowledge')}</TabsTrigger>
        </TabsList>

        <TabsContent value="mind" className="mt-4">
          <KnowledgeOverview />
        </TabsContent>

        {currentWorkspace?.id && (
          <TabsContent value="memory" className="mt-4">
            {/* Membros gravam; editar e esquecer vem de cada memória (AB#87149). */}
            <MemoryPanel target={{ scope: 'workspace', productId: currentWorkspace.id }} canCreate />
          </TabsContent>
        )}

        <TabsContent value="knowledge" className="mt-4">
          <Tabs
            value={location.knowledgeTab}
            className="w-full"
            onValueChange={(value) => goTo({ tab: 'knowledge', knowledgeTab: value as KnowledgeTab })}
          >
            <TabsList>
              <TabsTrigger value="files">
                {t('knowledge.storage.title')}
              </TabsTrigger>
              <TabsTrigger value="pipelines">
                {t('knowledge.pipelines.title')}
              </TabsTrigger>
              {isSuperAdmin && (
                <TabsTrigger value="embeddings">
                  {t('knowledge.embeddings.title')}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="files" className="mt-4">
              <KnowledgeStorage />
            </TabsContent>

            <TabsContent value="pipelines" className="mt-4">
              <KnowledgePipelines />
            </TabsContent>

            {isSuperAdmin && (
              <TabsContent value="embeddings" className="mt-4">
                {activeKnowledgeBase ? (
                  <KnowledgeEmbeddings knowledgeBaseId={activeKnowledgeBase.id} />
                ) : (
                  <div className="text-center py-4">
                    <p>{t('knowledge.embeddings.no_knowledge')}</p>
                  </div>
                )}
              </TabsContent>
            )}
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Knowledge;
